import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { IMG } from '../../views/landing/landingData';
import { BrandBtn, Icons, PillBtn, RoundBtn, Tip, type Theme } from './studioUi';

export type StudioTab = 'preview' | 'code' | 'media' | 'analytics';
export type Device = 'desktop' | 'tablet' | 'mobile';

interface StudioHeaderProps {
    projectName: string;
    onRename: (name: string) => void;
    tab: StudioTab;
    onTabChange: (tab: StudioTab) => void;
    device: Device;
    onDeviceChange: (device: Device) => void;
    /** Live URL of the published site, or null when it has never been published. */
    shareUrl: string | null;
    onExport: () => void;
    exporting: boolean;
    /** mailto: with the guided support template. */
    helpHref: string;
    published: boolean;
    onPublish: () => void;
    theme: Theme;
    onToggleTheme: () => void;
}

/**
 * Studio top bar from the handoff design: logo, inline-renamable project name,
 * Preview / Code / Media pill, device switcher, reload / share / export / help,
 * Publish, theme toggle and the avatar menu (Profile, Projects, Billing, Log out).
 */
export const StudioHeader: React.FC<StudioHeaderProps> = ({
    projectName, onRename, tab, onTabChange, device, onDeviceChange, shareUrl, onExport, exporting, helpHref, published, onPublish, theme, onToggleTheme,
}) => {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const isLight = theme === 'light';

    // Inline rename: Enter commits, Escape cancels, blur commits.
    const [renaming, setRenaming] = useState(false);
    const [draft, setDraft] = useState(projectName);
    const inputRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (renaming) inputRef.current?.select();
    }, [renaming]);
    const commit = () => {
        setRenaming(false);
        const next = draft.trim();
        if (next && next !== projectName) onRename(next);
    };

    // Share: copies the live URL (2 s "Link copied" feedback).
    const [copied, setCopied] = useState(false);
    const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const copyLink = async () => {
        if (!shareUrl) return;
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            if (copyTimer.current) clearTimeout(copyTimer.current);
            copyTimer.current = setTimeout(() => setCopied(false), 2000);
        } catch { /* clipboard unavailable */ }
    };
    useEffect(() => () => { if (copyTimer.current) clearTimeout(copyTimer.current); }, []);

    // Avatar menu.
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!menuOpen) return;
        const onOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
        };
        document.addEventListener('mousedown', onOutside, true);
        return () => document.removeEventListener('mousedown', onOutside, true);
    }, [menuOpen]);

    const seg = (active: boolean): React.CSSProperties => ({
        display: 'flex', alignItems: 'center', gap: 7, height: 30, padding: '0 14px', border: 0, borderRadius: 999, fontSize: 13, fontWeight: 700, cursor: 'pointer',
        background: active ? 'var(--hv-card)' : 'transparent', color: active ? 'var(--hv-mint-ink)' : 'var(--hv-body)',
    });
    const dev = (active: boolean): React.CSSProperties => ({
        width: 30, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 0, borderRadius: 999, cursor: 'pointer', padding: 0,
        background: active ? 'var(--hv-card)' : 'transparent', color: active ? 'var(--hv-mint-ink)' : 'var(--hv-body)',
    });
    const menuItem = (danger?: boolean): React.CSSProperties => ({
        width: '100%', display: 'flex', alignItems: 'center', gap: 9, padding: '9px 10px', border: 0, borderRadius: 8, background: 'transparent', fontSize: 13.5, fontWeight: 500,
        color: danger ? 'var(--hv-danger)' : 'var(--hv-ink)', cursor: 'pointer', textAlign: 'left',
    });
    const go = (to: string) => { setMenuOpen(false); navigate(to); };

    return (
        <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 12, height: 62, padding: '0 16px', borderBottom: '1px solid var(--hv-line)', background: 'var(--hv-card-2)' }}>
            <a href="/" aria-label="HyVay home" style={{ display: 'flex', flex: 'none', alignItems: 'center', gap: 8, color: 'var(--hv-ink)' }}>
                {isLight ? (
                    <img src={IMG.logoLight} alt="HyVay" style={{ height: 24, width: 24, display: 'block', objectFit: 'cover', mixBlendMode: 'multiply' }} />
                ) : (
                    <img src={IMG.logoDark} alt="HyVay" style={{ height: 24, width: 24, display: 'block', objectFit: 'cover' }} />
                )}
            </a>
            <span style={{ flex: 'none', width: 1, height: 22, background: 'var(--hv-line-2)' }} />

            <div className="hv-st-name" style={{ flex: 'none', display: 'flex', minWidth: 0 }}>
            {renaming ? (
                <input
                    ref={inputRef}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') commit();
                        if (e.key === 'Escape') { setDraft(projectName); setRenaming(false); }
                    }}
                    onBlur={commit}
                    aria-label="Project name"
                    style={{ flex: 'none', width: 230, height: 34, padding: '0 11px', borderRadius: 10, border: '1px solid var(--brand,#13422C)', background: 'var(--hv-card)', color: 'var(--hv-ink)', fontSize: 14, fontWeight: 700, outline: 'none' }}
                />
            ) : (
                <Tip label="Rename project" align="left" style={{ minWidth: 0 }}>
                    <button
                        type="button"
                        onClick={() => { setDraft(projectName); setRenaming(true); }}
                        className="hv-h-mint"
                        style={{ display: 'flex', alignItems: 'center', gap: 7, height: 34, padding: '0 11px', border: 0, borderRadius: 10, background: 'transparent', fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--hv-ink)', cursor: 'pointer', whiteSpace: 'nowrap', maxWidth: 230, overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{projectName}</span>
                        <span style={{ flex: 'none', display: 'flex' }}>{Icons.pencil({})}</span>
                    </button>
                </Tip>
            )}
            </div>

            <div style={{ flex: 1 }} />

            <div className="hv-st-tabs" style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 3, padding: 3, borderRadius: 999, background: 'var(--hv-page-2)', border: '1px solid var(--hv-line)' }}>
                <button type="button" onClick={() => onTabChange('preview')} style={seg(tab === 'preview')}>{Icons.eye({})}<span className="hv-st-tab-label">Preview</span></button>
                <button type="button" onClick={() => onTabChange('code')} style={seg(tab === 'code')}>{Icons.code({})}<span className="hv-st-tab-label">Code</span></button>
                <button type="button" onClick={() => onTabChange('media')} style={seg(tab === 'media')}>{Icons.media({})}<span className="hv-st-tab-label">Media</span></button>
                <button type="button" data-track="studio_analytics_tab" onClick={() => onTabChange('analytics')} style={seg(tab === 'analytics')}>{Icons.chart({})}<span className="hv-st-tab-label">Analytics</span></button>
            </div>

            <div style={{ flex: 1 }} />

            <div className="hv-st-devices" style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 2, padding: 3, borderRadius: 999, background: 'var(--hv-page-2)', border: '1px solid var(--hv-line)' }}>
                <Tip label="Desktop width"><button type="button" onClick={() => onDeviceChange('desktop')} aria-label="Desktop width" style={dev(device === 'desktop')}>{Icons.desktop({})}</button></Tip>
                <Tip label="Tablet width"><button type="button" onClick={() => onDeviceChange('tablet')} aria-label="Tablet width" style={dev(device === 'tablet')}>{Icons.tablet({})}</button></Tip>
                <Tip label="Mobile width"><button type="button" onClick={() => onDeviceChange('mobile')} aria-label="Mobile width" style={dev(device === 'mobile')}>{Icons.phone({})}</button></Tip>
            </div>

            <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                <Tip label="Reload studio">
                    <RoundBtn onClick={() => window.location.reload()} aria-label="Reload studio" className="hv-st-tool">{Icons.undo({})}</RoundBtn>
                </Tip>
                <Tip label={copied ? 'Link copied' : shareUrl ? 'Copy live link' : 'Publish first to get a link'}>
                    <RoundBtn data-track="studio_share" onClick={() => void copyLink()} aria-label="Share link" className="hv-st-tool" disabled={!shareUrl} style={shareUrl ? undefined : { opacity: 0.45, cursor: 'not-allowed' }}>{Icons.share({})}</RoundBtn>
                </Tip>
                {/* <Tip label="Download the source (.zip)">
                    <PillBtn className="hv-st-export" data-track="studio_export" onClick={onExport} disabled={exporting} style={exporting ? { opacity: 0.6, cursor: 'wait' } : undefined}>
                        {Icons.download({})}
                        {exporting ? 'Exporting…' : 'Export'}
                    </PillBtn>
                </Tip> */}
                <Tip label="Email support">
                    <a href={helpHref} className="hv-st-help hv-h-mint" style={{ display: 'flex', alignItems: 'center', gap: 7, height: 34, padding: '0 13px', borderRadius: 999, color: 'var(--hv-body)', fontSize: 13, fontWeight: 600 }}>
                        {Icons.help({})}
                        Help
                    </a>
                </Tip>
                <Tip label={published ? 'Republish your site' : 'Publish your site'}>
                    <BrandBtn data-track="studio_publish" onClick={onPublish} style={{ height: 34, padding: '0 16px', fontSize: 13, letterSpacing: '-0.005em' }} className="hv-h-brand-82">
                        {published ? 'Published' : 'Publish'}
                    </BrandBtn>
                </Tip>
                <Tip label={isLight ? 'Switch to dark mode' : 'Switch to light mode'} align="right">
                    <RoundBtn onClick={onToggleTheme} aria-label="Toggle theme" className="hv-st-theme">{isLight ? Icons.moon({}) : Icons.sun({})}</RoundBtn>
                </Tip>
                <div style={{ position: 'relative', display: 'flex' }} ref={menuRef}>
                    <Tip label="Account" align="right">
                        <button type="button" onClick={() => setMenuOpen((v) => !v)} aria-label="Account menu" style={{ border: 0, background: 'transparent', padding: 0, cursor: 'pointer', display: 'flex', lineHeight: 0 }}>
                            <img src={user?.avatar || IMG.avatarDefault} alt={user?.fullName || 'Account'} style={{ width: 32, height: 32, borderRadius: 999, objectFit: 'cover', border: '1px solid var(--hv-line)' }} />
                        </button>
                    </Tip>
                    {menuOpen && (
                        <div style={{ position: 'absolute', zIndex: 91, top: 'calc(100% + 10px)', right: 0, width: 200, padding: 6, borderRadius: 12, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 20px 40px -20px var(--hv-shadow)' }}>
                            <button type="button" onClick={() => go('/account/profile')} className="hv-h-mint" style={menuItem()}>{Icons.user({})} Profile</button>
                            <button type="button" onClick={() => go('/account/projects')} className="hv-h-mint" style={menuItem()}>{Icons.folder({})} Projects</button>
                            <button type="button" onClick={() => go('/account/billing')} className="hv-h-mint" style={menuItem()}>{Icons.card({})} Credits &amp; billing</button>
                            <div style={{ height: 1, background: 'var(--hv-line)', margin: '4px 6px' }} />
                            <button type="button" onClick={() => { setMenuOpen(false); logout(); navigate('/'); }} className="hv-h-danger-bg" style={menuItem(true)}>{Icons.logout({})} Log out</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
